import type { Workflow, AnalysisPrompt } from '@/shared/types'
import { API_URL, createHeaders } from './base'
import { fetchSseTicket, StreamEventHandler, StreamErrorHandler } from './sse'
import { SSEClient } from '../sseClient'
import type { StreamEvent } from './sse'

export interface ExternalPromptFile {
  path: string
  full_path: string
  name: string
  directory: string
  extension: string
  size: number
  modified: number
  framework: 'claude' | 'cursor'
}

export interface ExternalPromptsScanResult {
  files: ExternalPromptFile[]
  count: number
  directories_scanned: string[]
}

export async function getAnalysisPrompts(): Promise<AnalysisPrompt[]> {
  const response = await fetch(`${API_URL}/workflows/prompts`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch prompts')
  return response.json()
}

export async function scanExternalPrompts(repositoryPath: string = '.'): Promise<ExternalPromptsScanResult> {
  const response = await fetch(`${API_URL}/workflows/prompts/external?repository_path=${encodeURIComponent(repositoryPath)}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to scan external prompts')
  return response.json()
}

// Agent Scanners
export interface AgentScanner {
  name: string
  display_name: string
  available: boolean
}

export interface AgentScanRequest {
  repository_path: string
  prompt: string
  mode: string
  focus_area?: string
  scanner?: string
  agent_definition_id?: number
}

export async function getAgentScanners(): Promise<AgentScanner[]> {
  const response = await fetch(`${API_URL}/workflows/agent-scanners`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent scanners')
  const data = await response.json()
  return data.scanners
}

export async function triggerAgentScan(request: AgentScanRequest): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/agent-scan`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Agent scan failed' }))
    throw new Error(error.detail || 'Agent scan failed')
  }
  return response.json()
}

// Agent Executors
export interface AgentExecutor {
  name: string
  display_name: string
  available: boolean
}

export async function getAgentExecutors(): Promise<AgentExecutor[]> {
  const response = await fetch(`${API_URL}/workflows/agent-executors`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch agent executors')
  const data = await response.json()
  return data.executors
}

// Scan Profiles (Context Engine)
export interface ScanProfile {
  id: string
  name: string
  description: string
  strategy_type: 'pattern' | 'dependency_crawl' | 'git_diff' | 'discovery'
  icon?: string
  category?: string
}

export interface ProfileScanRequest {
  repository_path: string
  profile_id: string
}

// Discovery types
export interface MemoryDocument {
  path: string
  content: string
  doc_type: 'overview' | 'domain' | 'feature' | 'zone' | 'architecture'
  zone_name?: string
}

export interface ArchitectureZone {
  name: string
  path: string
  pattern_type: string
  key_structure: string
}

export interface ZoneComponent {
  name: string
  path: string
  responsibility: string
  key_files: string[]
}

export interface ZoneMapping {
  zone_name: string
  zone_path: string
  pattern_type: string
  components: ZoneComponent[]
}

export interface DiscoveryResult {
  type: 'discovery'
  architecture_type: string
  primary_languages: string[]
  zones: ArchitectureZone[]
  zone_mappings: ZoneMapping[]
  documents: MemoryDocument[]
}

export type ProfileScanResult = Workflow[] | DiscoveryResult

export async function getScanProfiles(): Promise<ScanProfile[]> {
  const response = await fetch(`${API_URL}/workflows/scan-profiles`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch scan profiles')
  const data = await response.json()
  return data.profiles
}

export async function getScanProfile(profileId: string): Promise<{
  id: string
  name: string
  description: string
  prompt_id: string
  strategy_type: 'pattern' | 'dependency_crawl' | 'git_diff' | 'discovery'
  strategy_config: Record<string, unknown>
  icon?: string
  category?: string
}> {
  const response = await fetch(`${API_URL}/workflows/scan-profiles/${profileId}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch scan profile')
  return response.json()
}

export async function triggerProfileScan(request: ProfileScanRequest): Promise<ProfileScanResult> {
  const response = await fetch(`${API_URL}/workflows/scan/profile`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Profile scan failed' }))
    throw new Error(error.detail || 'Profile scan failed')
  }
  return response.json()
}

export interface ProfilePreviewResult {
  files: string[]
  file_count: number
  strategy_type: string
  metadata: Record<string, unknown>
  content_size?: number
  raw_content?: string
}

export async function previewProfileScan(request: ProfileScanRequest): Promise<ProfilePreviewResult> {
  const response = await fetch(`${API_URL}/workflows/scan/profile/preview`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Profile preview failed' }))
    throw new Error(error.detail || 'Profile preview failed')
  }
  return response.json()
}

// Memory document save
export interface MemorySaveRequest {
  repository_path: string
  file_path: string
  content: string
}

export async function saveMemoryDocument(request: MemorySaveRequest): Promise<{
  success: boolean
  path: string
  message: string
}> {
  const response = await fetch(`${API_URL}/workflows/memory/save`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save memory document' }))
    throw new Error(error.detail || 'Failed to save memory document')
  }
  return response.json()
}

// ==========================================
// Scan Pipeline API (Unix-style composable scanning)
// ==========================================

export interface PipelineGatherRequest {
  repository_path: string
  strategy_type: string
  strategy_config: Record<string, unknown>
}

export interface PipelineAnalyzeRequest {
  analyzer_type: string
  prompt_id?: string
  agent_definition_id?: number
  focus_area?: string
}

export interface PipelineCreateWorkflowsRequest {
  finding_indices: number[]
}

export interface PipelineScanContext {
  id: number
  strategy_type: string
  strategy_config: Record<string, unknown>
  repository_path: string
  status: 'gathering' | 'ready' | 'failed'
  files: string[]
  raw_content: string
  content_size: number
  file_count: number
  metadata: Record<string, unknown>
  error?: string
  created_at: string
  completed_at?: string
}

export interface PipelineScanAnalysis {
  id: number
  context_id: number
  analyzer_type: string
  prompt_id?: string
  agent_definition_id?: number
  status: 'pending' | 'analyzing' | 'ready' | 'failed'
  findings: PipelineFinding[]
  raw_llm_output?: string
  stats: Record<string, unknown>
  error?: string
  created_at: string
  completed_at?: string
}

export interface PipelineAuditResult {
  verified: boolean
  confidence: number
  risk_level: 'low' | 'medium' | 'high' | 'critical'
  issues: string[]
  notes: string
}

export interface PipelineFinding {
  title: string
  description: string
  type: 'feature' | 'improvement' | 'refactor' | 'bugfix' | 'security' | 'performance'
  priority: 'low' | 'medium' | 'high' | 'critical'
  target_files: string[]
  suggested_changes?: Record<string, unknown>
  implementation_plan?: Record<string, unknown>
  audit_status?: 'unaudited' | 'verified' | 'disputed' | 'rejected'
  audit_result?: PipelineAuditResult
}

export interface PipelineContextSummary {
  id: number
  strategy_type: string
  repository_path: string
  status: string
  file_count: number
  content_size: number
  created_at: string
  completed_at?: string
}

export interface PipelineAnalysisSummary {
  id: number
  context_id: number
  analyzer_type: string
  prompt_id?: string
  status: string
  finding_count: number
  created_at: string
  completed_at?: string
}

export interface PipelineStrategy {
  name: string
  class: string
}

// Stage 1: Gather Context
export async function pipelineGatherContext(request: PipelineGatherRequest): Promise<PipelineScanContext> {
  const response = await fetch(`${API_URL}/pipeline/context`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Failed to gather context')
  }
  return response.json()
}

export async function pipelineListContexts(status?: string, strategyType?: string): Promise<PipelineContextSummary[]> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (strategyType) params.set('strategy_type', strategyType)

  const response = await fetch(`${API_URL}/pipeline/context?${params.toString()}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to list contexts')
  return response.json()
}

export async function pipelineGetContext(contextId: number): Promise<PipelineScanContext> {
  const response = await fetch(`${API_URL}/pipeline/context/${contextId}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get context')
  return response.json()
}

export async function pipelineGetContextContent(contextId: number): Promise<string> {
  const response = await fetch(`${API_URL}/pipeline/context/${contextId}/content`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get context content')
  return response.text()
}

export async function pipelineGetContextFiles(contextId: number): Promise<{ context_id: number; file_count: number; files: string[] }> {
  const response = await fetch(`${API_URL}/pipeline/context/${contextId}/files`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get context files')
  return response.json()
}

export async function pipelineDeleteContext(contextId: number): Promise<void> {
  const response = await fetch(`${API_URL}/pipeline/context/${contextId}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete context')
}

export async function pipelineGetStrategies(): Promise<{ strategies: PipelineStrategy[] }> {
  const response = await fetch(`${API_URL}/pipeline/strategies`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get strategies')
  return response.json()
}

// Stage 2: Analyze Context
export async function pipelineAnalyzeContext(contextId: number, request: PipelineAnalyzeRequest): Promise<PipelineScanAnalysis> {
  const response = await fetch(`${API_URL}/pipeline/context/${contextId}/analyze`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Analysis failed')
  }
  return response.json()
}

export function pipelineAnalyzeContextStream(
  contextId: number,
  request: PipelineAnalyzeRequest,
  onEvent: StreamEventHandler,
  onError?: StreamErrorHandler
): () => void {
  const params = new URLSearchParams()
  params.set('analyzer_type', request.analyzer_type)
  if (request.prompt_id) params.set('prompt_id', request.prompt_id)
  if (request.agent_definition_id) params.set('agent_definition_id', String(request.agent_definition_id))
  if (request.focus_area) params.set('focus_area', request.focus_area)

  const url = `${API_URL}/pipeline/context/${contextId}/analyze/stream?${params.toString()}`

  // Create SSE client with reconnection support
  const client = new SSEClient<StreamEvent>(url, {
    maxRetries: 10,
    initialDelay: 1000,
    maxDelay: 30000,
    fetchSseTicket,
  })

  client.addEventListener(onEvent)
  if (onError) {
    client.addErrorListener(onError)
  }
  client.connect()

  // Return cleanup function
  return () => client.disconnect()
}

export async function pipelineListAnalyses(contextId?: number, status?: string): Promise<PipelineAnalysisSummary[]> {
  const params = new URLSearchParams()
  if (contextId) params.set('context_id', String(contextId))
  if (status) params.set('status', status)

  const response = await fetch(`${API_URL}/pipeline/analysis?${params.toString()}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to list analyses')
  return response.json()
}

export async function pipelineGetAnalysis(analysisId: number): Promise<PipelineScanAnalysis> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get analysis')
  return response.json()
}

export async function pipelineGetAnalysisFindings(analysisId: number): Promise<{ analysis_id: number; finding_count: number; findings: PipelineFinding[] }> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}/findings`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to get findings')
  return response.json()
}

export async function pipelineDeleteAnalysis(analysisId: number): Promise<void> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete analysis')
}

// Audit Findings
export async function pipelineAuditFinding(analysisId: number, findingIndex: number): Promise<PipelineFinding> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}/findings/${findingIndex}/audit`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Audit failed')
  }
  return response.json()
}

export async function pipelineAuditFindings(analysisId: number, findingIndices: number[]): Promise<{ analysis_id: number; findings: PipelineFinding[] }> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}/audit`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ finding_indices: findingIndices }),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Bulk audit failed')
  }
  return response.json()
}

// Stage 3: Create Workflows
export async function pipelineCreateWorkflows(analysisId: number, request: PipelineCreateWorkflowsRequest): Promise<Workflow[]> {
  const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}/workflows`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Failed to create workflows')
  }
  return response.json()
}

// Convenience: Full Pipeline
export async function pipelineRunFull(
  gatherRequest: PipelineGatherRequest,
  analyzerType: string = 'gemini',
  promptId?: string,
  createWorkflows: boolean = true
): Promise<{
  context_id: number
  analysis_id: number
  finding_count: number
  workflow_count: number
  workflow_ids: number[]
}> {
  const params = new URLSearchParams()
  params.set('analyzer_type', analyzerType)
  if (promptId) params.set('prompt_id', promptId)
  params.set('create_workflows', String(createWorkflows))

  const response = await fetch(`${API_URL}/pipeline/run?${params.toString()}`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(gatherRequest),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Pipeline failed')
  }
  return response.json()
}

// ==========================================
// Pipeline Definitions API
// ==========================================

export interface PipelineDefinitionRead {
  id: number
  name: string
  description: string | null
  nodes: unknown[]
  edges: unknown[]
  created_at: string
  updated_at: string
}

export interface PipelineDefinitionCreate {
  name: string
  description?: string
  nodes: unknown[]
  edges: unknown[]
}

export interface PipelineDefinitionApiUpdate {
  name?: string
  description?: string
  nodes?: unknown[]
  edges?: unknown[]
}

export async function getPipelineDefinitions(): Promise<PipelineDefinitionRead[]> {
  const response = await fetch(`${API_URL}/pipeline-definitions/`, { headers: createHeaders() })
  if (!response.ok) throw new Error('Failed to fetch pipeline definitions')
  return response.json()
}

export async function getPipelineDefinition(id: number): Promise<PipelineDefinitionRead> {
  const response = await fetch(`${API_URL}/pipeline-definitions/${id}`, { headers: createHeaders() })
  if (!response.ok) throw new Error('Failed to fetch pipeline definition')
  return response.json()
}

export async function createPipelineDefinition(data: PipelineDefinitionCreate): Promise<PipelineDefinitionRead> {
  const response = await fetch(`${API_URL}/pipeline-definitions/`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) throw new Error('Failed to create pipeline definition')
  return response.json()
}

export async function updatePipelineDefinition(id: number, data: PipelineDefinitionApiUpdate): Promise<PipelineDefinitionRead> {
  const response = await fetch(`${API_URL}/pipeline-definitions/${id}`, {
    method: 'PUT',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) throw new Error('Failed to update pipeline definition')
  return response.json()
}

export async function deletePipelineDefinition(id: number): Promise<void> {
  const response = await fetch(`${API_URL}/pipeline-definitions/${id}`, {
    method: 'DELETE',
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to delete pipeline definition')
}
